import React from 'react';

import { ChangeHandler } from 'src/presentation/components/pages/integrations/types';
import CompactSection from 'src/presentation/components/pages/integrations/editorComponents/CompactSection';
import { ExternalSourceType, ExternalSourceTypes } from 'src/actions/integrations/integrationsResource';
import { Row } from 'src/presentation/components/forms/Layout';
import {
  MaskedField,
  WrappedField,
} from 'src/presentation/components/pages/integrations/editorComponents/EditorFields';

function renderMagentoConfiguration(
  onChange: (attr: string) => ChangeHandler,
  maskedFields: string[],
  integrationType: ExternalSourceType
): JSX.Element {
  const deepLinkUrlRow =
    integrationType === ExternalSourceTypes.Magento ? (
      <Row>
        <WrappedField
          attr="connectionSettings.deepLinkUrl"
          label="Magento Deep Link URL (optional)"
          description={`If the URL that is shown when viewing an order in Magento follows a different pattern
                            than your base URL, you can specify it here`}
          placeholder="e.g. https://magento.company.com/orders"
          data-aid="magento-deep-link-url"
          onChange={onChange('connectionSettings.deepLinkUrl')}
        />
      </Row>
    ) : null;

  return (
    <>
      <CompactSection
        description={`The Magento app enriches your customer pages with customer details and order 
                      information pulled from your Magento instance.`}
      >
        <Row>
          <WrappedField
            attr="name"
            label="Name"
            placeholder="App Name"
            description="This is the name that will be displayed throughout Gladly"
            data-aid="magento-integration-name"
            autoFocus={true}
            onChange={onChange('name')}
          />
        </Row>
        <Row>
          <WrappedField
            attr="connectionSettings.baseUrl"
            label="Magento Base URL"
            description="This is the URL of your Magento instance"
            placeholder="e.g. https://magento.company.com"
            data-aid="magento-base-url"
            onChange={onChange('connectionSettings.baseUrl')}
          />
        </Row>
        {deepLinkUrlRow}
      </CompactSection>
      <CompactSection
        header="Authentication"
        description={`Passwords and keys used to connect to other accounts are treated as secrets in Gladly.
               If you need to change an existing secret, use the clear button in the field to remove the current
               secret and enter a new one`}
      >
        <Row>
          <MaskedField
            attr="secrets.consumer.key"
            label="Consumer Key"
            showClearButton={maskedFields.includes('consumer.key')}
            isMasked={maskedFields.includes('consumer.key')}
            data-aid="magento-consumer-key"
            onChange={onChange('secrets.consumer.key')}
          />
        </Row>
        <Row>
          <MaskedField
            attr="secrets.consumer.secret"
            label="Consumer Secret"
            showClearButton={maskedFields.includes('consumer.secret')}
            isMasked={maskedFields.includes('consumer.secret')}
            data-aid="magento-consumer-secret"
            onChange={onChange('secrets.consumer.secret')}
          />
        </Row>
        <Row>
          <MaskedField
            attr="secrets.token.key"
            label="Access Token"
            showClearButton={maskedFields.includes('token.key')}
            isMasked={maskedFields.includes('token.key')}
            data-aid="magento-magento-key"
            onChange={onChange('secrets.token.key')}
          />
        </Row>
        <Row>
          <MaskedField
            attr="secrets.token.secret"
            label="Access Token Secret"
            showClearButton={maskedFields.includes('token.secret')}
            isMasked={maskedFields.includes('token.secret')}
            data-aid="magento-magento-secret"
            onChange={onChange('secrets.token.secret')}
          />
        </Row>
      </CompactSection>
    </>
  );
}

export default renderMagentoConfiguration;
